import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import type { FunnelStatus } from 'src/api/generated';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import type { TagDropdownOption } from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { createElement } from 'react';
import { StatusDropdownGroupHeader } from '../components/molecules/status-dropdown-group-header';

export const AI_STATUSES_HEADER_ID = '__ai-statuses-header__';

/**
 * Minimal status fields needed to build dropdown options.
 */
export interface StatusDropdownOptionStatus
  extends Pick<FunnelStatus, 'id' | 'name' | 'terminalOutcome'> {}

/**
 * Build grouped conversation status dropdown options with manual statuses first.
 *
 * When both manual and AI-linked statuses are present, the dropdown renders
 * two labeled groups so agents see the operational statuses first and the
 * AI-managed statuses afterward.
 *
 * @param statuses - Ordered funnel statuses available in the current funnel.
 * @param selectedStatusId - Currently selected status id.
 * @param aiStatusIds - Status ids linked to AI automations.
 * @returns Dropdown options ready for the shared TagDropdown component.
 */
export const buildConversationStatusDropdownOptions = (
  statuses: StatusDropdownOptionStatus[],
  selectedStatusId: number | undefined,
  aiStatusIds: number[],
  areAiStatusesCollapsed = true
): TagDropdownOption[] => {
  const aiStatusIdSet = new Set(aiStatusIds);

  const manualOptions = statuses
    .filter((status) => !aiStatusIdSet.has(status.id))
    .map((status) => ({
      id: status.id,
      content: createElement(FunnelStatusName, {
        name: status.name,
        outcome: status.terminalOutcome,
        icon: 'archive',
        showArchiveTooltip: true,
      }),
      selected: status.id === selectedStatusId,
    }));

  const aiOptions = statuses
    .filter((status) => aiStatusIdSet.has(status.id))
    .map((status) => ({
      id: status.id,
      content: createElement(FunnelStatusName, {
        name: status.name,
        outcome: status.terminalOutcome,
        icon: 'archive',
        showArchiveTooltip: true,
      }),
      selected: status.id === selectedStatusId,
    }));

  if (manualOptions.length === 0 || aiOptions.length === 0) {
    return [...manualOptions, ...aiOptions];
  }

  const groupedManualOptions = manualOptions.map((option, index) => ({
    ...option,
    separator: index === manualOptions.length - 1,
  }));

  return [
    ...groupedManualOptions,
    createCollapsibleGroupHeaderOption(
      AI_STATUSES_HEADER_ID,
      'Estados IA',
      aiOptions.length,
      areAiStatusesCollapsed
    ),
    ...(areAiStatusesCollapsed ? [] : aiOptions),
  ];
};

/**
 * Checks whether a dropdown option id belongs to a non-interactive group
 * header row.
 *
 * @param optionId - Dropdown option identifier.
 * @returns True when the option is one of the status group headers.
 */
export const isStatusDropdownGroupHeader = (
  optionId: TagDropdownOption['id']
): boolean => optionId === AI_STATUSES_HEADER_ID;

const createCollapsibleGroupHeaderOption = (
  id: string,
  label: string,
  itemCount: number,
  isCollapsed: boolean
): TagDropdownOption => ({
  id,
  closeOnClick: false,
  content: createElement(StatusDropdownGroupHeader, {
    label,
    icon: createElement(AiRobotOutlineIcon, {
      className: 'h-4 w-4 text-secondary-500',
    }),
    itemCount,
    isCollapsed,
  }),
});
